// vim: set fdm=marker :
// {{{ import
import { useEffect, useReducer, useCallback, useMemo } from 'react';
import { startOfDay, endOfDay, endOfYear, subDays } from 'date-fns';
import * as Sentry from '@sentry/browser';

import { setState } from '../actions';
import keyValueReducer from '../reducers/keyValueReducer';
// }}}

const getMemberInitials = (fullName = 'Unknown User') => {
  let initials = fullName.split(' ');
  initials =
    initials.shift().charAt(0) +
    (initials.length ? initials.pop().charAt(0) : '');
  return initials.toUpperCase();
};

const getDefaultWorklogsBy = () => {
  try {
    const groupWorklogsBy = localStorage.getItem('groupWorklogsBy') ?? 'card';
    if (groupWorklogsBy === 'null') {
      return null;
    }
    return groupWorklogsBy;
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
  }
  return 'card';
};

// {{{ initialState{}
const initialState = {
  isSelfWorklogs: null,
  date: {
    minDate: startOfDay(new Date(2018, 0, 1)),
    maxDate: endOfYear(new Date()),
    ranges: [
      {
        startDate: startOfDay(subDays(new Date(), 31)),
        endDate: endOfDay(new Date()),
        key: 'selection',
      },
    ],
  },
  groupWorklogsBy: getDefaultWorklogsBy(),
  labels: {
    all: [],
    selected: [],
    cardIds: [],
  },
  members: {
    all: [],
    selected: [],
  },
  cards: {
    all: [],
    selected: [],
  },
  lists: {
    all: [],
    selected: [],
  },
};
// }}}

const useWorklogsFilter = ({
  boardId,
  cardId,
  memberId,
  isAllowToSeeOthers,
  weekStartsOn,
  usersEntities,
  usersIds,
  cardsEntities,
  cardsIds,
  listsEntities,
  listsIds,
  labelsEntities,
  labelsIds,
}) => {
  const [state, dispatch] = useReducer(keyValueReducer, {
    ...initialState,
    groupWorklogsBy: cardId ? null : initialState.groupWorklogsBy,
  });
  // {{{ onChangeFilter
  const onChangeFilter = useCallback((filterName, selected) => {
    switch (filterName) {
      case 'groupWorklogsBy': {
        return dispatch(
          setState({
            groupWorklogsBy: selected,
          })
        );
      }
      case 'isSelfWorklogs': {
        return dispatch(
          setState({
            isSelfWorklogs: selected,
          })
        );
      }
      case 'date':
        return dispatch(
          setState({
            date: {
              ranges: [selected],
              _merge: true,
            },
          })
        );
      case 'members':
      case 'labels':
      case 'cards':
      case 'lists':
      default:
        return dispatch(
          setState({
            [filterName]: {
              selected,
              _merge: true,
            },
          })
        );
    }
  }, []);
  // }}}

  // {{{ requestParams()
  const requestParams = useMemo(() => {
    if (cardId) {
      return {
        ...(state.members.selected.length
          ? { accountIds: state.members.selected.map((m) => m.value) }
          : {}),
        cardIds: [cardId],
        timezoneOffset: new Date().getTimezoneOffset(),
        weekStartsOn,
      };
    }
    const selectedLabels = state.labels.selected.map((label) => label.value);
    const selectedLists = state.lists.selected.map((list) => list.value);
    const filterCardsIds = state.cards.selected
      .map((card) => card.value)
      .concat(
        cardsIds.filter(
          (cId) =>
            selectedLists.some(
              (listId) => listId === cardsEntities[cId].idList
            ) ||
            selectedLabels.some((labelId) =>
              (cardsEntities[cId]?.idLabels ?? []).includes(labelId)
            )
        )
      );
    return {
      startedFrom: state.date.ranges[0].startDate.toISOString(),
      startedTo: state.date.ranges[0].endDate.toISOString(),
      boardIds: [boardId],
      timezoneOffset: new Date().getTimezoneOffset(),
      weekStartsOn,
      groupWorklogsBy: state.groupWorklogsBy,

      ...(state.isSelfWorklogs ? { accountIds: [memberId] } : {}),
      ...(state.members.selected.length
        ? { accountIds: state.members.selected.map((m) => m.value) }
        : {}),
      ...(!isAllowToSeeOthers ? { accountIds: [memberId] } : {}),
      ...(filterCardsIds.length ? { cardIds: filterCardsIds } : {}),
    };
  }, [
    state.date.ranges,
    state.members.selected,
    state.labels.selected,
    state.lists.selected,
    state.cards.selected,
    state.isSelfWorklogs,
    state.groupWorklogsBy,
    boardId,
    cardId,
    memberId,
    isAllowToSeeOthers,
    weekStartsOn,
  ]);
  // }}}

  // {{{ Prepare filter options effect
  useEffect(() => {
    dispatch(
      setState({
        cards: {
          all: cardsIds.map((id) => {
            const { id: value, name } = cardsEntities[id];
            return {
              value,
              name,
            };
          }),
          _merge: true,
        },
        members: {
          all: usersIds.map((userId) => {
            const { username, fullName, avatarUrl } = usersEntities[userId];
            return {
              value: userId,
              username,
              fullName,
              avatarUrl: avatarUrl && `${avatarUrl}/170.png`,
              initials: getMemberInitials(fullName),
            };
          }),
          _merge: true,
        },
        labels: {
          all: labelsIds?.map((labelId) => {
            const { color, name, id } = labelsEntities[labelId];
            return {
              value: id,
              color,
              name,
            };
          }),
          _merge: true,
        },
        lists: {
          all: listsIds?.map((listId) => {
            const { name, id } = listsEntities[listId];
            return {
              value: id,
              name,
            };
          }),
          _merge: true,
        },
      })
    );
  }, [
    usersEntities,
    usersIds,
    cardsEntities,
    cardsIds,
    labelsEntities,
    labelsIds,
    listsEntities,
    listsIds,
  ]);
  // }}}

  return [
    state,
    {
      onChangeFilter,
      requestParams,
    },
  ];
};

export default useWorklogsFilter;
